import { App } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import React, { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { useHistory } from "react-router";
import { Events, Params, useAnalytics } from "./AppAnalyticsProvider";
import { useAppState } from "./AppListenerProvider";
import PreferencesService from "./services/preferences.service";

interface DeepLinkProviderProps {
    children: ReactNode;
}

interface DeepLinkData {
    slug: string | null;
    referrer: string | null;
}

interface DeepLinkContextType {
    deepLinkData: DeepLinkData;
    clearDeepLinkData: () => Promise<void>;
}

const DeepLinkContext = createContext<DeepLinkContextType | undefined>(undefined);

/**
 * Bucketize a deep-link slug into a low-cardinality link_type for breakdowns.
 * Raw slugs/URLs can be high-cardinality and include user IDs (PII), so the
 * deep_link_target param logs the bucketized path, not the raw one.
 */
const classifyLink = (slug: string): { linkType: string; target: string } => {
    if (slug.startsWith('/share')) return { linkType: 'referral', target: 'share' };
    if (slug.startsWith('/clip')) return { linkType: 'share', target: 'clip' };
    if (slug.startsWith('/profile')) return { linkType: 'share', target: 'profile' };
    if (slug.startsWith('/crews')) return { linkType: 'share', target: 'crew' };
    if (slug.startsWith('/event')) return { linkType: 'share', target: 'event' };
    if (slug.startsWith('/shop')) return { linkType: 'promo', target: 'shop' };
    if (slug.startsWith('/purchase')) return { linkType: 'promo', target: 'purchase' };
    return { linkType: 'universal', target: 'other' };
};

export const DeepLinkProvider: React.FC<DeepLinkProviderProps> = ({ children }) => {
    const { isNative } = useAppState();
    const { trackEvent } = useAnalytics();
    let history = useHistory();
    const [deepLinkData, setDeepLinkData] = useState<DeepLinkData>({ slug: null, referrer: null });

    const clearDeepLinkData = useCallback(async () => {
        await PreferencesService.removeDeepLinkSlug();
        await PreferencesService.removeReferrer();
        setDeepLinkData({ slug: null, referrer: null });
    }, []);

    useEffect(() => {
        const loadInitialData = async () => {
            const slug = await PreferencesService.getDeepLinkSlug();
            const referrer = await PreferencesService.getReferrer();
            setDeepLinkData({ slug, referrer });
        };
        loadInitialData();

        const handleDeepLink = async (url: string) => {
            console.log("deep link url: ", url);

            let slug = '';
            if (url.includes('style')) {
                slug = url.split("style").pop();
            } else if (url.includes('live')) {
                slug = url.split("live").pop();
            } else if (url.includes('day')) {
                slug = url.split("day").pop();
            } else {
                console.log("Unrecognized URL format");
                return;
            }

            console.log("Extracted slug:", slug);

            // Fire DEEP_LINK_OPENED for every successfully-parsed deep link.
            // Powers the deep link / referral panels on the dashboard —
            // previously there was NO signal for "user opened a deep link."
            const { linkType, target } = classifyLink(slug);
            trackEvent(Events.DEEP_LINK_OPENED, {
                [Params.LINK_TYPE]: linkType,
                [Params.DEEP_LINK_TARGET]: target,
            });

            let referrer = null;
            if (slug.startsWith('/share')) {
                // USED FOR REFERRALS
                const params = new URLSearchParams(slug.split('?')[1]);
                referrer = params.get('username');

                await PreferencesService.setDeepLinkSlug(slug);
                if (referrer) {
                    await PreferencesService.setReferrer(referrer);
                } else {
                    await PreferencesService.removeReferrer();
                }

                setDeepLinkData({ slug, referrer });
            } else {
                // USED FOR IN APP LINKS
                history.push(slug);
            }

            // Assuming isNative is true for mobile platforms
            if (isNative) {
                await Browser.close();
            }
        };

        const listener = App.addListener("appUrlOpen", async ({ url }) => {
            await handleDeepLink(url);
        });

        return () => {
            listener.then(f => f.remove());
        };
    }, []);

    return (
        <DeepLinkContext.Provider value={{ deepLinkData, clearDeepLinkData }}>
            {children}
        </DeepLinkContext.Provider>
    );
};

export const useDeepLink = () => {
    const context = useContext(DeepLinkContext);
    if (context === undefined) {
        throw new Error('useDeepLink must be used within a DeepLinkProvider');
    }
    return context;
};